import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CreditCardIcon } from '@heroicons/react/24/outline'
import type { Subscription } from '../types'
import { FREE_TIER_LIMITS } from '../types'
import { createBillingPortalSession } from '../services/subscriptionRepo'
import { getEffectivePlan } from '../utils/subscription'
import { formatDate } from '../utils/dateFormat'
import { BillingConfirmationModal } from './BillingConfirmationModal'
import { trackSubscribe, trackViewPricing } from '../utils/facebookPixel'
import { trackGAPurchase, trackGAViewPricing } from '../utils/googleAnalytics'
import { trackLinkedInPurchase } from '../utils/linkedinTracking'
import { PageLayout } from './PageLayout'

type Props = {
  userId: string
  subscription: Subscription | null
  activeAppCount: number
  resumeAnalysesCount: number
  quizzesCount: number
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'
  onRefreshSubscription: () => Promise<void>
}

export const BillingPage = ({ userId, subscription, activeAppCount, resumeAnalysesCount, quizzesCount, dateFormat, onRefreshSubscription }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [modalType, setModalType] = useState<'success' | 'cancelled' | null>(null)
  const navigate = useNavigate()

  const plan = getEffectivePlan(subscription)
  const isPro = plan === 'pro'

  useEffect(() => {
    const success = searchParams.get('success')
    const cancelled = searchParams.get('cancelled')
    const purchasedPlan = searchParams.get('plan') as 'monthly' | 'quarterly' | 'lifetime' | null
    const purchaseAmount = searchParams.get('amount')
    const purchaseEventId = searchParams.get('event_id')

    if (success === 'true') {
      setModalType('success')

      // Track conversion events
      if (purchasedPlan && purchaseAmount) {
        const amountNum = parseFloat(purchaseAmount)
        if (!isNaN(amountNum)) {
          // Facebook Pixel - Subscribe event (with eventID for CAPI dedup)
          trackSubscribe(purchasedPlan, amountNum, subscription?.stripeSubscriptionId || undefined, purchaseEventId || undefined)
          
          // Google Analytics - Purchase event
          trackGAPurchase(purchasedPlan, amountNum, subscription?.stripeSubscriptionId || undefined)

          // LinkedIn - Purchase conversion
          trackLinkedInPurchase()
        }
      }

      // Refetch subscription on success
      onRefreshSubscription().catch(err => {
        console.error('Failed to refresh subscription:', err)
      })
    } else if (cancelled === 'true') {
      setModalType('cancelled')
    } else {
      // No query params = close modal
      setModalType(null)
    }
  }, [searchParams, onRefreshSubscription, subscription?.stripeSubscriptionId])

  const handleManageBilling = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const url = await createBillingPortalSession(
        userId,
        `${window.location.origin}/billing`
      )
      window.location.href = url
    } catch (err) {
      console.error('Billing portal error:', err)
      setError(err instanceof Error ? err.message : 'Failed to open billing portal')
      setIsLoading(false)
    }
  }

  const handleCloseModal = () => {
    setModalType(null)
    setSearchParams({}) // Remove query params from URL
  }

  return (
    <PageLayout
      icon={CreditCardIcon}
      title="Billing"
      subtitle="Manage your subscription and billing"
    >
      <div className="card p-6 space-y-6">
          {/* Current Plan */}
          <div className={`rounded-lg border p-4 ${
            isPro
              ? 'border-emerald-300 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/30'
              : 'border-app-line bg-app-subtle'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-app-ink text-lg">
                    {isPro ? 'Pro Plan' : 'Free Plan'}
                  </p>
                  {isPro && (
                    <span className="rounded-full bg-emerald-200 dark:bg-emerald-900/50 px-2 py-0.5 text-xs text-emerald-800 dark:text-emerald-300">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-app-muted mt-1">
                  {isPro
                    ? 'You have access to all features and unlimited applications.'
                    : 'Track up to 10 applications for free.'}
                </p>
              </div>
              {isPro && subscription?.currentPeriodEnd && (
                <div className="text-right text-sm">
                  <p className="text-app-muted">
                    {subscription.cancelAtPeriodEnd ? 'Expires' : 'Renews'}
                  </p>
                  <p className="text-app-ink font-medium">
                    {formatDate(subscription.currentPeriodEnd, dateFormat)}
                  </p>
                </div>
              )}
            </div>

            {/* Usage progress (Free tier only) */}
            {!isPro && (
              <div className="mt-4 pt-4 border-t border-app-line space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-app-muted">Active applications</span>
                    <span className="text-app-ink font-medium">
                      {activeAppCount} / {FREE_TIER_LIMITS.maxActiveApps}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-app-subtle overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        activeAppCount >= FREE_TIER_LIMITS.maxActiveApps
                          ? 'bg-rose-500'
                          : activeAppCount >= FREE_TIER_LIMITS.maxActiveApps * 0.8
                            ? 'bg-amber-500'
                            : 'bg-sky-500 dark:bg-app-primary'
                      }`}
                      style={{ width: `${Math.min(100, (activeAppCount / FREE_TIER_LIMITS.maxActiveApps) * 100)}%` }}
                    />
                  </div>
                  {activeAppCount >= FREE_TIER_LIMITS.maxActiveApps && (
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                      You've reached the free tier limit. Upgrade to add more applications.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-app-muted">Resume analyses</span>
                    <span className="text-app-ink font-medium">
                      {resumeAnalysesCount} / {FREE_TIER_LIMITS.maxResumeAnalysesPerMonth}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-app-subtle overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        resumeAnalysesCount >= FREE_TIER_LIMITS.maxResumeAnalysesPerMonth
                          ? 'bg-rose-500'
                          : resumeAnalysesCount >= FREE_TIER_LIMITS.maxResumeAnalysesPerMonth * 0.8
                            ? 'bg-amber-500'
                            : 'bg-sky-500 dark:bg-app-primary'
                      }`}
                      style={{ width: `${Math.min(100, (resumeAnalysesCount / FREE_TIER_LIMITS.maxResumeAnalysesPerMonth) * 100)}%` }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-app-muted">Interview quizzes</span>
                    <span className="text-app-ink font-medium">
                      {quizzesCount} / {FREE_TIER_LIMITS.maxQuizzes}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-app-subtle overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        quizzesCount >= FREE_TIER_LIMITS.maxQuizzes
                          ? 'bg-rose-500'
                          : quizzesCount >= FREE_TIER_LIMITS.maxQuizzes * 0.8
                            ? 'bg-amber-500'
                            : 'bg-sky-500 dark:bg-app-primary'
                      }`}
                      style={{ width: `${Math.min(100, (quizzesCount / FREE_TIER_LIMITS.maxQuizzes) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Manage subscription button (Pro only) */}
            {isPro && (
              <div className="mt-4 pt-4 border-t border-app-line">
                <button
                  onClick={handleManageBilling}
                  disabled={isLoading}
                  className="rounded-lg border border-app-line bg-app-page px-4 py-2 text-sm font-semibold text-app-ink hover:bg-app-subtle disabled:opacity-60 transition-colors"
                >
                  {isLoading ? 'Loading...' : 'Manage Subscription'}
                </button>
              </div>
            )}
          </div>

          {/* Upgrade prompt (Free tier only) */}
          {!isPro && (
            <div className="rounded-lg border-2 border-sky-200 dark:border-app-line bg-sky-50 dark:bg-app-highlight p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-app-ink">Ready to unlock all features?</p>
                  <p className="text-sm text-app-muted mt-1">
                    Get unlimited applications, advanced analytics, multi-currency conversion and more.
                  </p>
                </div>
                <button
                  onClick={() => {
                    trackViewPricing('billing_page', 'upgrade_prompt')
                    trackGAViewPricing('billing_page', 'upgrade_prompt')
                    navigate('/pricing')
                  }}
                  className="shrink-0 rounded-lg bg-app-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-app-primary-hover transition-colors shadow-app-card"
                >
                  View Plans
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-4 py-3">
              <p className="text-sm text-rose-700 dark:text-rose-200">{error}</p>
            </div>
          )}

          {/* FAQ */}
          <div className="border-t border-app-line pt-6">
            <p className="font-semibold text-app-ink mb-4">Frequently Asked Questions</p>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-app-ink font-medium">Can I cancel anytime?</p>
                <p className="text-app-muted mt-1">
                  Yes! You can cancel your subscription at any time. You'll keep access until the end of your billing period.
                </p>
              </div>
              <div>
                <p className="text-app-ink font-medium">What happens to my data if I downgrade?</p>
                <p className="text-app-muted mt-1">
                  Your data is safe. On free plan, you can view all applications but can only have {FREE_TIER_LIMITS.maxActiveApps} active at a time.
                </p>
              </div>
              <div>
                <p className="text-app-ink font-medium">Do you offer refunds?</p>
                <p className="text-app-muted mt-1">
                  Yes, we offer a 14-day money-back guarantee. Contact us if you're not satisfied.
                </p>
              </div>
            </div>
          </div>
        </div>
      <BillingConfirmationModal
        isOpen={modalType !== null}
        type={modalType}
        onClose={handleCloseModal}
      />
    </PageLayout>
  )
}
